import React, { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import DemoBadge from "../components/DemoBadge";
import KPI from "../components/KPI";
import PulseDot from "../components/PulseDot";
import AnimatedMetric from "../components/AnimatedMetric";
import WaterProductionPanel from "../components/WaterProductionPanel";
import ArcGauge from "../components/ArcGauge";
import PulseMap from "../components/PulseMap";
import CitizenImpactPanel from "../components/CitizenImpactPanel";
import FiscalOverviewPanel from "../components/FiscalOverviewPanel";
import TrafficHotspots from "../components/TrafficHotspots";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  cyberApi, dashApi, budgetApi, recApi, demoApi, engineApi,
} from "../lib/api";
import { toast } from "sonner";
import { Play, Pause, RotateCcw } from "lucide-react";

const ENTITY_FOR = {
  small_city: "City of Cedar Falls",
  county: "Montrose County",
  data_center: "City of Cedar Falls",
  school_district: "Riverbend School District",
};
const FISCAL_FOR = {
  small_city: "City of Cedar Falls",
  county: "Manatee County",
  data_center: "City of Cedar Falls",
  school_district: "Manatee County",
};
const LOOP_SECONDS = 20;

const Demo = () => {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState("small_city");
  const [summary, setSummary] = useState(null);
  const [cyber, setCyber] = useState(null);
  const [scan, setScan] = useState(null);
  const [water, setWater] = useState(null);
  const [flags, setFlags] = useState([]);
  const [recs, setRecs] = useState([]);
  const [engines, setEngines] = useState([]);
  const [alertsFeed, setAlertsFeed] = useState([]);
  const [running, setRunning] = useState(true);
  const [ticks, setTicks] = useState(0);
  const [healthScore, setHealthScore] = useState(70);
  const intervalRef = useRef(null);
  const loopRef = useRef(null);

  useEffect(() => {
    demoApi.scenarios().then(r => setScenarios(r.scenarios || []));
    loadStatic("City of Cedar Falls");
  }, []);

  const loadStatic = (entity) => {
    dashApi.summary().then(setSummary);
    budgetApi.flags(entity).then(d => setFlags(d.risk_flags || []));
    recApi.prioritized(entity).then(d => setRecs(d.recommendations || []));
  };

  useEffect(() => {
    if (!running) { clearInterval(intervalRef.current); return; }
    const tick = async () => {
      try {
        const [c, s, w, e] = await Promise.all([
          cyberApi.status(), cyberApi.scan(), engineApi.water(), engineApi.status(),
        ]);
        setCyber(c); setScan(s); setWater(w); setEngines(e.engines || []);
        setTicks(t => t + 1);
        setHealthScore(87 + Math.round((Math.random() * 2 - 1) * 4));
        if (Math.random() < 0.55) {
          const sources = [
            { label: "CYBER",   msg: "Unauthorized access attempt blocked on PLC-WTP-02", tone: "critical" },
            { label: "WATER",   msg: "AWG-03 filter warning — efficiency 65% (target 100%)", tone: "gold" },
            { label: "BUDGET",  msg: "Department variance refreshed — 2 new review items", tone: "electric" },
            { label: "TRAFFIC", msg: "Crosstown Pkwy congestion +14% — recommendation queued", tone: "gold" },
            { label: "POLICY",  msg: "AuditIQ policy sync completed — Zero-Trust v3.2", tone: "electric" },
            { label: "AI",      msg: "Risk model tick — 412 signals processed", tone: "electric" },
          ];
          const a = sources[Math.floor(Math.random() * sources.length)];
          setAlertsFeed(feed => [{ ...a, ts: new Date().toLocaleTimeString() }, ...feed].slice(0, 14));
        }
      } catch (e) {
        // Resilient demo loop — log but never bubble up. The next interval tick
        // recovers automatically. (Iter 19h: silent-catch hardening.)
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV !== "production") console.warn("[Demo alerts feed]", e);
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 2500);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => {
    if (!running) { clearInterval(loopRef.current); return; }
    loopRef.current = setInterval(() => {
      setActive(prev => {
        const keys = ["small_city", "county", "data_center", "school_district"];
        const idx = keys.indexOf(prev);
        const next = keys[(idx + 1) % keys.length];
        loadStatic(ENTITY_FOR[next]);
        return next;
      });
    }, LOOP_SECONDS * 1000);
    return () => clearInterval(loopRef.current);
  }, [running]);

  const switchScenario = async (key) => {
    setActive(key);
    loadStatic(ENTITY_FOR[key] || "City of Cedar Falls");
    try { await demoApi.start(key); toast.success(`Scenario loaded: ${key}`); }
    catch { toast.error("Demo scenario failed to start"); }
  };

  const reset = () => {
    setAlertsFeed([]); setTicks(0); setActive("small_city");
    toast.success("Demo scenario reset");
    loadStatic("City of Cedar Falls");
  };

  const entity = ENTITY_FOR[active];
  const fiscalEntity = FISCAL_FOR[active];

  return (
    <Layout>
      <SEOHead title="Interactive Demo Mode | AuditIQ" path="/demo"
        description="AuditIQ live demo — interactive control room with infrastructure pulse map, citizen impact savings, traffic hotspots, cybersecurity, and water production." />
      <PageHeader eyebrow="Demo Mode" demo
        title="Control room. Live data. Zero spreadsheets."
        subtitle="Auto-looping simulation of AuditIQ's nine intelligence engines. Scenarios rotate every 20 seconds — pause, reset, or switch manually at any time."
        actions={
          <div className="flex gap-2">
            <Button className="btn-gold h-10 px-5" onClick={() => setRunning(true)} data-testid="demo-play">
              <Play size={14} className="mr-2" /> Start
            </Button>
            <Button className="btn-ghost-gold h-10 px-5" onClick={() => setRunning(false)} data-testid="demo-pause">
              <Pause size={14} className="mr-2" /> Pause
            </Button>
            <Button className="btn-ghost-gold h-10 px-5" onClick={reset} data-testid="demo-reset">
              <RotateCcw size={14} className="mr-2" /> Reset
            </Button>
          </div>
        }
      />

      <section className="border-y border-gold-dim/25 bg-navy-deep/60 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-6 whitespace-nowrap overflow-x-auto">
          {engines.length === 0 ? (
            <span className="text-xs text-steel">Booting engines…</span>
          ) : engines.map(e => (
            <div key={e.id} className="flex items-center gap-2 text-xs shrink-0" data-testid={`eng-${e.id}`}>
              <PulseDot tone={e.status === "healthy" ? "electric" : "critical"} />
              <span className="text-cream">{e.name}</span>
              <span className="text-steel">· {e.response_time_ms}ms · {e.throughput}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.24em] text-cream-dim">
            Auto-loop: scenario rotates every {LOOP_SECONDS}s <span className={running ? "text-[color:var(--auditiq-success)]" : "text-[color:var(--auditiq-warning)]"}>· {running ? "running" : "paused"}</span>
          </div>
          <div className="text-[11px] text-cream-dim">Entity: <span className="text-gold">{entity}</span></div>
        </div>
        <div className="flex flex-wrap gap-3" data-testid="scenario-list">
          {scenarios.map(s => (
            <button key={s.key} onClick={() => switchScenario(s.key)}
              data-testid={`scenario-${s.key}`}
              className={`px-4 py-3 border text-left transition-colors rounded ${active === s.key
                ? "border-electric bg-navy-soft" : "border-gold-dim/30 bg-navy-deep/40 hover:border-gold"}`}>
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold flex items-center gap-2">
                {active === s.key && <PulseDot />} {s.key}
              </div>
              <div className="font-display text-base text-cream mt-1">{s.title}</div>
              <div className="text-xs text-cream-dim mt-1 max-w-xs">{s.description}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-16">
        <Tabs defaultValue="operations" className="w-full">
          <TabsList className="bg-navy-deep border border-gold-dim/30 mb-6 flex-wrap h-auto">
            <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">AuditIQ Dashboard</TabsTrigger>
            <TabsTrigger value="cyber" data-testid="tab-cyber">Cybersecurity</TabsTrigger>
            <TabsTrigger value="water" data-testid="tab-water">Water Production</TabsTrigger>
            <TabsTrigger value="traffic" data-testid="tab-traffic">Traffic</TabsTrigger>
            <TabsTrigger value="fiscal" data-testid="tab-fiscal">Fiscal</TabsTrigger>
            <TabsTrigger value="scanner" data-testid="tab-scanner">Budget</TabsTrigger>
            <TabsTrigger value="recs" data-testid="tab-recs">Recommendations</TabsTrigger>
          </TabsList>

          <TabsContent value="operations">
            <div className="grid lg:grid-cols-[300px_1fr_300px] gap-5 mb-5">
              <div className="glass p-5 flex flex-col items-center justify-center">
                <div className="text-[11px] uppercase tracking-[0.22em] text-electric mb-2 flex items-center gap-2">
                  <PulseDot /> System Health Score
                </div>
                <ArcGauge value={healthScore} max={100} label="Infrastructure · Water · Cyber" testid="system-health" />
                <div className="mt-3 text-xs text-[color:var(--auditiq-success)]">↑ 12 pts vs. last month</div>
                <div className="grid grid-cols-3 gap-2 mt-4 w-full">
                  <Tiny label="Treatment" value="92" />
                  <Tiny label="Distribution" value="85" />
                  <Tiny label="Infra" value="78" />
                </div>
              </div>
              <PulseMap refreshMs={3000} />
              <div className="glass p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
                    <PulseDot /> Active Alerts
                  </div>
                  <span className="text-xs bg-[rgba(229,62,62,0.18)] text-[color:var(--auditiq-critical)] px-2 py-0.5 rounded font-mono">
                    {alertsFeed.length}
                  </span>
                </div>
                <ul className="space-y-2 text-sm max-h-96 overflow-auto" data-testid="alert-feed">
                  {alertsFeed.length === 0
                    ? <li className="text-steel text-xs">Waiting for first tick…</li>
                    : alertsFeed.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 border-b border-gold-dim/10 pb-2">
                        <PulseDot tone={a.tone} />
                        <div className="flex-1">
                          <div className="text-[10px] uppercase tracking-wider text-gold">{a.label}</div>
                          <div className="text-cream-dim text-xs">{a.msg}</div>
                        </div>
                        <span className="text-[10px] text-steel font-mono">{a.ts}</span>
                      </li>
                    ))
                  }
                </ul>
              </div>
            </div>

            <CitizenImpactPanel entity={entity} key={`impact-${active}`} />
          </TabsContent>

          <TabsContent value="dashboard">
            <div className="grid md:grid-cols-4 gap-5 mb-6">
              <KPI label="Entities" value={summary?.entities_tracked ?? "—"} testid="demo-kpi-entities" />
              <KPI label="Risk Flags" value={summary?.risk_flags_total ?? "—"} accent="warning" testid="demo-kpi-flags" />
              <KPI label="Protected Devices" value={summary?.water_devices ?? "—"} accent="success" testid="demo-kpi-devices" />
              <KPI label="Live Ticks" value={ticks} testid="demo-kpi-ticks" sublabel="simulation pulses" />
            </div>
            <div className="grid md:grid-cols-[1fr_1fr] gap-5">
              <div className="glass p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
                  <PulseDot /> 60-Second Cyber Scan
                </div>
                <div className="data-line mt-2 mb-4" />
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Mini label="Next scan in" value={`${scan?.seconds_until_next_scan ?? "—"}s`} />
                  <Mini label="Scans today" value={scan?.scans_completed_today} />
                  <Mini label="Devices scanned" value={scan?.devices_scanned_last_cycle} />
                  <Mini label="Threats detected" value={scan?.threats_detected_today} tone="warning" />
                  <Mini label="Threats blocked" value={scan?.threats_blocked_today} tone="success" />
                  <Mini label="Config drifts" value={scan?.config_drifts_detected} />
                  <Mini label="API calls monitored" value={scan?.api_calls_monitored} />
                  <Mini label="Signatures checked" value={scan?.malware_signatures_checked} />
                </div>
              </div>
              <div className="glass p-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-electric flex items-center gap-2">
                  <PulseDot /> Risk Flags · {entity}
                </div>
                <div className="data-line mt-2 mb-4" />
                <ul className="space-y-3 text-sm max-h-80 overflow-auto">
                  {flags.map(f => (
                    <li key={f.id} className="border-l-2 pl-3" style={{
                      borderColor: f.severity === "high" ? "var(--auditiq-critical)" : "var(--auditiq-warning)" }}>
                      <div className="text-cream">{f.flag_type} — {f.category}</div>
                      <div className="text-xs text-cream-dim">{f.description}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="cyber">
            <div className="grid md:grid-cols-4 gap-5 mb-6">
              <KPI label="Monitor" value={cyber?.monitor_status?.toUpperCase() || "—"} accent="success" testid="demo-cyber-mon" />
              <KPI label="Device Trust" value={cyber?.avg_device_trust_score ?? "—"} testid="demo-cyber-trust" />
              <KPI label="Unauthorized (24h)" value={cyber?.unauthorized_attempts_24h ?? "—"} accent="warning" testid="demo-cyber-unauth" />
              <KPI label="Critical Devices" value={cyber?.critical_devices ?? "—"} accent="critical" testid="demo-cyber-crit" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="glass p-6">
                <h4 className="font-display text-lg mb-2">Incident Queue <DemoBadge /></h4>
                <ul className="space-y-2 text-sm">
                  {(cyber?.incident_queue || []).map(i => (
                    <li key={i.id} className="flex items-center justify-between border-b border-gold-dim/15 py-2">
                      <span className="text-cream">{i.event_type}</span>
                      <span className="text-xs text-steel">{i.target_device}</span>
                      <span className="text-[10px] uppercase text-gold">{i.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass p-6">
                <h4 className="font-display text-lg mb-2">Recommended Interventions <DemoBadge /></h4>
                <ul className="space-y-2 text-sm">
                  {(cyber?.recommended_interventions || []).map((r, i) => (
                    <li key={i} className="border-l-2 border-gold pl-3">
                      <div className="text-cream">{r.directive}</div>
                      <div className="text-xs text-cream-dim">severity: {r.severity} · human approval: {String(r.requires_human_approval)}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="water">
            <WaterProductionPanel data={water} />
          </TabsContent>

          <TabsContent value="traffic">
            <TrafficHotspots city="Port St. Lucie, FL" />
          </TabsContent>

          <TabsContent value="fiscal">
            <FiscalOverviewPanel entity={fiscalEntity} key={`fiscal-${active}`} />
          </TabsContent>

          <TabsContent value="scanner">
            <div className="glass p-6">
              <h4 className="font-display text-lg mb-3">Risk Flags — {entity} <DemoBadge /></h4>
              <ul className="space-y-3">
                {flags.map(f => (
                  <li key={f.id} className="border-l-2 pl-3" style={{
                    borderColor: f.severity === "high" ? "var(--auditiq-critical)" : "var(--auditiq-warning)" }}>
                    <div className="text-cream">{f.flag_type} — {f.category}</div>
                    <div className="text-xs text-cream-dim">{f.description}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold mt-1">{f.language}</div>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="recs">
            <div className="grid md:grid-cols-2 gap-5">
              {recs.map(r => (
                <div key={r.id} className="glass p-6">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{r.language}</div>
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${
                      r.priority === "high" ? "bg-[rgba(229,62,62,0.15)] text-[color:var(--auditiq-critical)]" :
                      r.priority === "medium" ? "bg-[rgba(236,201,75,0.15)] text-[color:var(--auditiq-warning)]" :
                      "bg-[rgba(47,133,90,0.15)] text-[color:var(--auditiq-success)]"
                    }`}>{r.priority} priority</span>
                  </div>
                  <div className="font-display text-lg text-cream mt-2">{r.title}</div>
                  <div className="text-sm text-cream-dim mt-2">{r.issue}</div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <Mini label="Reallocation" value={`$${(r.suggested_amount_usd/1e3).toFixed(0)}k`} />
                    <Mini label="Impact score" value={r.impact_score} />
                    <Mini label="Est. savings" value={`$${Math.round(r.estimated_cost_savings_usd/1e3)}k`} tone="success" />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
};

const Tiny = ({ label, value }) => (
  <div className="panel p-2 text-center">
    <div className="text-[9px] uppercase tracking-[0.22em] text-steel">{label}</div>
    <div className="metric text-lg text-cream">{value}</div>
  </div>
);

const Mini = ({ label, value, tone }) => (
  <div className="panel p-3">
    <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{label}</div>
    <div className={`metric text-xl mt-1 ${
      tone === "warning" ? "text-[color:var(--auditiq-warning)]" :
      tone === "success" ? "text-[color:var(--auditiq-success)]" :
      "text-cream"}`}>
      <AnimatedMetric value={Number(value) || 0} decimals={0} />
      {typeof value === "string" && isNaN(Number(value)) ? value : null}
    </div>
  </div>
);

export default Demo;
